import React from 'react';
import { AlertTriangle } from 'lucide-react';

// Only parseApiboo puts folders at the top level; Postman and OpenAPI keep them under `settings`
// (Insomnia, Bruno and HAR have none). Reading just `col.folders` made the folder count dead code.
const folderCount = (col) => col.folders?.length ?? col.settings?.folders?.length ?? 0;

const countLine = (t, col) => t('import.requestCount', { count: col.requests?.length ?? 0 })
  + (folderCount(col) ? ` · ${t('import.folderCount', { count: folderCount(col), defaultValue: '{{count}} folders' })}` : '');

/**
 * What the chosen file contains, flat: ONE collection → a name field (it is what you would
 * rename anyway) with its size under it; SEVERAL → rows to tick, each renameable once ticked.
 * An environment file → each environment's name and how many variables. Environments that came
 * WITH a collection (Bruno) → one checkbox, on by default. Notes from the parser at the end.
 */
function Notes({ warnings, t }) {
  if (!warnings?.length) return null;
  return (
    <div className="imp-notes">
      <div className="imp-notes__title"><AlertTriangle size={13} aria-hidden />{t('import.warnings')}</div>
      {/* eslint-disable-next-line react/no-array-index-key -- de-duplicated strings, never reordered */}
      <ul>{[...new Set(warnings)].slice(0, 8).map((w, i) => <li key={i}>{w}</li>)}</ul>
    </div>
  );
}

export default function ImportPreview({ preview, selected, names, busy, onToggle, onRename, includeEnvironments = true, onToggleEnvironments, t }) {
  if (preview.kind === 'environment') {
    const envs = preview.environments?.length ? preview.environments : [preview.environment];
    return (
      <>
        {envs.map((env, i) => (
          // eslint-disable-next-line react/no-array-index-key -- the file's own order; names may repeat
          <div className="imp-row" key={i}>
            <div className="min-w-0 flex-1">
              <div className="imp-row__name">{t('import.environmentLabel')} · {env.name}</div>
              <div className="imp-count">{t('import.variableCount', { count: env.variables.length })}</div>
            </div>
          </div>
        ))}
        <Notes warnings={preview.warnings} t={t} />
      </>
    );
  }
  const { collections, warnings } = preview;
  const envCount = preview.environments?.length || 0;
  const single = collections.length === 1;
  return (
    <>
      {single ? (
        <div>
          <label className="modal-logo-label" htmlFor="imp-name" style={{ display: 'block', marginBottom: '0.375rem' }}>{t('import.collectionName')}</label>
          <input
            id="imp-name"
            type="text"
            className="modal-input"
            disabled={busy}
            value={names[0] ?? collections[0].name}
            onChange={(e) => onRename(0, e.target.value)}
          />
          <div className="imp-count">{countLine(t, collections[0])}</div>
        </div>
      ) : (
        <div className="imp-list panel-scroll">
          {collections.map((col, i) => {
            const on = selected.has(i);
            return (
              // eslint-disable-next-line react/no-array-index-key -- the file's own order; names may repeat
              <div className="imp-row" key={i}>
                <input type="checkbox" className="checkbox-brand imp-row__check" checked={on} disabled={busy} onChange={() => onToggle(i)} aria-label={col.name} />
                <div className="min-w-0 flex-1">
                  <div className="imp-row__name">{col.name}</div>
                  <div className="imp-count">{countLine(t, col)}</div>
                  {on && (
                    <input
                      type="text"
                      className="modal-input"
                      disabled={busy}
                      placeholder={col.name}
                      value={names[i] ?? ''}
                      onChange={(e) => onRename(i, e.target.value)}
                      aria-label={t('import.renameOptional')}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {envCount > 0 && (
        <label className="imp-envs">
          <input
            type="checkbox"
            className="checkbox-brand"
            checked={includeEnvironments}
            disabled={busy}
            onChange={() => onToggleEnvironments?.()}
          />
          {t('import.alsoImportEnvironments', { count: envCount })}
        </label>
      )}
      <Notes warnings={warnings} t={t} />
    </>
  );
}
